import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, DatePicker, Empty, Input, Modal, Spin, Switch, Tooltip, Typography, message } from 'antd';
import { CloseOutlined, InfoCircleOutlined, LeftOutlined, QuestionCircleOutlined, RightOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
    GET_ORDER_QUOTA_DETAIL,
    GRID_DAYS,
    IMPORT_ORDER_QUOTA,
    OrderQuotaDetail,
    OrderQuotaImportResult,
    OrderQuotaImportRowInput,
    OrderQuotaRow,
    OrderQuotaStrategy,
    OrderQuotaStrategyType,
    SET_ORDER_QUOTA_DAILY_OVERRIDES,
    SET_ORDER_QUOTA_HUB_CAPACITY,
    SET_ORDER_QUOTA_RULE_DAILY_QUOTA,
    SET_ORDER_QUOTA_RULE_PAUSED,
    SET_ORDER_QUOTA_SWITCH,
} from '../graphql/orderQuotaGraphql';
import {
    applyOverrides,
    buildDateColumns,
    replaceRow,
    rowMaxValue,
    shiftDate,
} from '../utils/orderQuotaGrid';
import BatchOverrideModal from './BatchOverrideModal';
import ImportQuotaModal from './ImportQuotaModal';
import LineList from './LineList';
import OrderQuotaGrid from './OrderQuotaGrid';
import QuotaCell from './QuotaCell';

interface OrderQuotaWorkspaceProps {
    strategyType: OrderQuotaStrategyType;
    hubId: string;
    /** 页面级操作（如客户分级导入）后递增，工作区据此重新拉取 */
    reloadKey?: number;
    /** 开关、HUB 产能变化后通知左侧列表刷新 */
    onChanged: () => void;
}

type HubCapacityField = 'baseCapacity' | 'transferReserve' | 'floatCapacity';

type HubCapacityValues = Record<HubCapacityField, number>;

type HubCapacityResult = { setOnboardingOrderQuotaHubCapacity: { hubDailyQuota: number; transferReserve: number; floatCapacity: number } };

const capacityOf = (detail: OrderQuotaDetail): HubCapacityValues => ({
    baseCapacity: detail.hubDailyQuota,
    transferReserve: detail.transferReserve,
    floatCapacity: detail.floatCapacity,
});

/** 多行报错提示条的 key：再次失败时替换为新的一条，手动关闭、切换 HUB 或离开页面时按它关闭 */
const MULTILINE_ERROR_KEY = 'order-quota-multiline-error';

const closeMultilineError = () => message.destroy(MULTILINE_ERROR_KEY);

/**
 * 后端报错可能有多行（如容量校验每处一行）。报错统一用顶部正中的提示条：
 * 多行时首行为标题、其余每行一条，内容过高时滚动，不自动消失，右侧 × 手动关闭；单行 3 秒后消失
 */
const showError = (text: string) => {
    if (!text.includes('\n')) {
        message.error(text);
        return;
    }
    const [title, ...details] = text.split('\n');
    message.error({
        key: MULTILINE_ERROR_KEY,
        duration: 0,
        /* 图标与标题顶端对齐，默认垂直居中时会落在多行内容中间 */
        className: '[&_.ant-message-custom-content]:!items-start [&_.ant-message-custom-content>.anticon]:mt-1',
        content: (
            <span className="inline-flex items-start gap-3 max-w-[560px] text-left align-top">
                <span>
                    <span className="block font-medium">{title}</span>
                    <span className="block max-h-72 overflow-y-auto">
                        <LineList lines={details} />
                    </span>
                </span>
                <CloseOutlined className="mt-1 text-gray-400 hover:text-gray-600 cursor-pointer" onClick={closeMultilineError} />
            </span>
        ),
    });
};

/**
 * 某个 HUB 的产能工作区：开关、HUB 三项（仅分拣产能管理）、规则说明、工具栏与产能网格。
 * 分拣产能管理的客户行由分级名单生成，不提供添加 / 移除客户与暂停，分级入口在页面顶部；尾程产能管理保留暂停。
 * 单元格保存成功后就地更新本地数据，不整页刷新。
 */
const OrderQuotaWorkspace: React.FC<OrderQuotaWorkspaceProps> = ({ strategyType, hubId, reloadKey, onChanged }) => {
    const { t } = useTranslation();
    const [startDate, setStartDate] = useState<string | undefined>(undefined);
    const [rows, setRows] = useState<OrderQuotaRow[]>([]);
    const [expandedTargetId, setExpandedTargetId] = useState<string>();
    const [keyword, setKeyword] = useState('');
    const [batchOpen, setBatchOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);

    useEffect(() => {
        setStartDate(undefined);
        setExpandedTargetId(undefined);
        setKeyword('');
    }, [hubId]);

    /* 上一个 HUB 的报错不留在页面上：切换 HUB 或离开页面时关闭 */
    useEffect(() => closeMultilineError, [hubId]);

    const { data, loading, refetch } = useQuery<{ onboardingOrderQuotaDetail: OrderQuotaDetail }>(GET_ORDER_QUOTA_DETAIL, {
        variables: { strategyType, hubId, startDate, days: GRID_DAYS },
        fetchPolicy: 'network-only',
        onError: (error) => message.error(error.message),
    });
    const detail = data?.onboardingOrderQuotaDetail;

    useEffect(() => {
        if (reloadKey) refetch();
    }, [reloadKey, refetch]);

    useEffect(() => {
        setRows(detail?.rows ?? []);
    }, [detail]);

    /**
     * HUB 三项已保存成功的最新值：保存时其余两项取这里而不是上一次拉取的 detail，
     * 避免连续修改时后一次用旧值把前一次改回去；没有保存在途时随重新拉取的数据同步
     */
    const savedCapacity = useRef<{ hubId: string; values: HubCapacityValues } | null>(null);
    /** HUB 三项的保存依次执行：前一次返回后再提交下一次，避免后发先至 */
    const capacityQueue = useRef<Promise<void>>(Promise.resolve());
    const pendingCapacitySaves = useRef(0);

    useEffect(() => {
        if (detail && pendingCapacitySaves.current === 0) savedCapacity.current = { hubId: detail.hubId, values: capacityOf(detail) };
    }, [detail]);

    const [setSwitch] = useMutation(SET_ORDER_QUOTA_SWITCH);
    const [setHubCapacity] = useMutation<HubCapacityResult>(SET_ORDER_QUOTA_HUB_CAPACITY);
    const [setRuleDailyQuota] = useMutation(SET_ORDER_QUOTA_RULE_DAILY_QUOTA);
    const [setRulePaused] = useMutation(SET_ORDER_QUOTA_RULE_PAUSED);
    const [setDailyOverrides] = useMutation(SET_ORDER_QUOTA_DAILY_OVERRIDES);
    const [importQuota] = useMutation<{ importOnboardingOrderQuota: OrderQuotaImportResult }>(IMPORT_ORDER_QUOTA);

    /** 执行修改：失败时提示后端返回的原因并向调用方抛出（单元格据此撤销输入） */
    const run = useCallback(
        async <T,>(action: () => Promise<T>, savedHint = true): Promise<T> => {
            try {
                const result = await action();
                if (savedHint) message.success(t('central.orderQuota.saved', { time: dayjs().format('HH:mm:ss') }));
                return result;
            } catch (e) {
                showError((e as Error).message);
                throw e;
            }
        },
        [t]
    );

    const dateColumns = useMemo(
        () => (detail ? buildDateColumns(detail.startDate, detail.endDate, detail.today) : []),
        [detail]
    );
    const visibleRows = useMemo(() => {
        const text = keyword.trim().toLowerCase();
        if (!text) return rows;
        return rows.filter((row) => `${row.targetName} ${row.targetCode} ${row.targetId}`.toLowerCase().includes(text));
    }, [rows, keyword]);
    /* 操作入口只在展开行上，按日批量设置作用于该行 */
    const selectedRow = rows.find((row) => row.targetId === expandedTargetId);
    const toggleExpand = useCallback(
        (targetId: string) => setExpandedTargetId((current) => (current === targetId ? undefined : targetId)),
        []
    );
    const openBatch = useCallback(() => setBatchOpen(true), []);
    const isSorting = strategyType === OrderQuotaStrategy.CUSTOMER_PICKUP_HUB;

    const commitDailyQuota = useCallback(
        async (row: OrderQuotaRow, value: number) => {
            await run(() => setRuleDailyQuota({ variables: { strategyType, hubId, targetId: row.targetId, dailyQuota: value } }));
            setRows((current) => replaceRow(current, row.targetId, (r) => ({ ...r, dailyQuota: value, configured: true })));
        },
        [run, setRuleDailyQuota, strategyType, hubId]
    );

    const commitOverride = useCallback(
        async (row: OrderQuotaRow, date: string, value: number | null) => {
            await run(() =>
                setDailyOverrides({
                    variables: { strategyType, hubId, targetId: row.targetId, startDate: date, endDate: date, dailyQuota: value },
                })
            );
            setRows((current) => replaceRow(current, row.targetId, (r) => applyOverrides(r, [date], value)));
        },
        [run, setDailyOverrides, strategyType, hubId]
    );

    const reloadAll = async () => {
        await refetch();
        onChanged();
    };

    const toggleSwitch = (enabled: boolean) => {
        const apply = async () => {
            await run(() => setSwitch({ variables: { strategyType, hubId, enabled } }));
            await reloadAll();
        };
        if (enabled && isSorting && detail?.hubDailyQuota === 0) {
            Modal.confirm({
                title: t('central.orderQuota.switch.zeroBaseTitle'),
                content: t('central.orderQuota.switch.zeroBaseContent'),
                okText: t('common.confirm'),
                cancelText: t('common.cancel'),
                onOk: apply,
            });
            return;
        }
        if (enabled) {
            apply();
            return;
        }
        Modal.confirm({
            title: t('central.orderQuota.switch.offTitle'),
            content: t(`central.orderQuota.switch.offContent.${strategyType}`),
            okText: t('common.confirm'),
            cancelText: t('common.cancel'),
            onOk: apply,
        });
    };

    const togglePaused = useCallback(
        async (row: OrderQuotaRow) => {
            const paused = !row.isPaused;
            await run(() => setRulePaused({ variables: { strategyType, hubId, targetId: row.targetId, paused } }));
            setRows((current) => replaceRow(current, row.targetId, (r) => ({ ...r, isPaused: paused, configured: true })));
        },
        [run, setRulePaused, strategyType, hubId]
    );

    /** HUB 三项一次提交：修改其中一项时其余两项取已保存的最新值；多次修改排队依次提交 */
    const commitHubCapacity = (field: HubCapacityField, value: number): Promise<void> => {
        if (!detail) return Promise.resolve();
        const fallback = capacityOf(detail);
        pendingCapacitySaves.current += 1;
        const task = capacityQueue.current.then(async () => {
            try {
                const latest = savedCapacity.current?.hubId === hubId ? savedCapacity.current.values : fallback;
                const values = { ...latest, [field]: value };
                const result = await run(() => setHubCapacity({ variables: { hubId, ...values } }));
                const saved = result.data?.setOnboardingOrderQuotaHubCapacity;
                if (savedCapacity.current?.hubId === hubId) {
                    savedCapacity.current = {
                        hubId,
                        values: saved
                            ? { baseCapacity: saved.hubDailyQuota, transferReserve: saved.transferReserve, floatCapacity: saved.floatCapacity }
                            : values,
                    };
                }
            } finally {
                pendingCapacitySaves.current -= 1;
            }
        });
        /* 失败只影响本次（单元格据此撤销输入），不阻塞后续排队的保存 */
        capacityQueue.current = task.catch(() => undefined);
        return task.then(reloadAll);
    };

    const submitBatch = async (start: string, end: string, dailyQuota: number | null) => {
        if (!selectedRow) return;
        await run(() =>
            setDailyOverrides({ variables: { strategyType, hubId, targetId: selectedRow.targetId, startDate: start, endDate: end, dailyQuota } })
        );
        setBatchOpen(false);
        await refetch();
    };


    const submitImport = async (importRows: OrderQuotaImportRowInput[], dryRun: boolean) => {
        const result = await importQuota({ variables: { strategyType, hubId, rows: importRows, dryRun } });
        const payload = result.data!.importOnboardingOrderQuota;
        if (payload.applied) await reloadAll();
        return payload;
    };

    if (!detail) {
        return loading ? (
            <div className="flex justify-center py-10"><Spin /></div>
        ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        );
    }

    /* 与原型一致：标题只显示一个 HUB 标识，用 HUB 名称（分拣码只用于系统识别），名称为空时用编码 */
    const title = detail.hubName || detail.hubCode || '';
    /* 按原型分两行：基础产能与浮动产能一行，转运预留一行，每行下方附说明 */
    const capacityRows: { fields: { field: HubCapacityField; value: number }[]; help: string }[] = [
        {
            fields: [
                { field: 'baseCapacity', value: detail.hubDailyQuota },
                { field: 'floatCapacity', value: detail.floatCapacity },
            ],
            help: 'central.orderQuota.capacity.baseHelp',
        },
        {
            fields: [{ field: 'transferReserve', value: detail.transferReserve }],
            help: 'central.orderQuota.capacity.reserveHelp',
        },
    ];

    return (
        <div className="flex flex-col gap-4 min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
                <span className="text-base font-semibold tracking-wide">{title}</span>
                <div className="ml-auto flex items-center gap-2 text-[13px]">
                    <span>{t('central.orderQuota.switch.label')}</span>
                    <Switch checked={detail.enabled} onChange={toggleSwitch} />
                    <span className={`min-w-[42px] ${detail.enabled ? 'text-[#722ed1]' : 'text-[#8c8c8c]'}`}>
                        {t(detail.enabled ? 'central.orderQuota.switch.on' : 'central.orderQuota.switch.off')}
                    </span>
                </div>
            </div>
            {!detail.enabled && (
                <div className="flex items-center gap-2 px-3 py-2 rounded border border-solid border-[#f0f0f0] bg-[#fafafa] text-[13px] text-[#595959]">
                    <InfoCircleOutlined className="text-[#8c8c8c]" />
                    <span>{t(`central.orderQuota.switch.offBanner.${strategyType}`, { hub: title })}</span>
                </div>
            )}
            {isSorting && (
                <div className={`flex flex-col gap-3.5 ${detail.enabled ? '' : 'opacity-50'}`}>
                    {capacityRows.map(({ fields, help }) => (
                        <div key={help} className="flex flex-col gap-1.5">
                            <div className="flex flex-wrap items-end gap-x-7 gap-y-3">
                                {fields.map(({ field, value }) => (
                                    <div key={field} className="flex flex-col gap-1.5">
                                        <span className="text-[13px] text-[#595959]">{t(`central.orderQuota.capacity.${field}`)}</span>
                                        <div className="flex items-center gap-2">
                                            <div className="w-[150px]">
                                                <QuotaCell
                                                    value={value}
                                                    allowEmpty={false}
                                                    unsetUnlimited={false}
                                                    size="middle"
                                                    inputClassName="text-right text-base font-semibold tabular-nums"
                                                    onCommit={(next) => commitHubCapacity(field, next ?? 0)}
                                                />
                                            </div>
                                            <span className="text-xs text-[#8c8c8c]">{t('central.orderQuota.capacity.unit')}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <span className="text-xs text-[#8c8c8c] leading-[1.7]">{t(help)}</span>
                        </div>
                    ))}
                </div>
            )}
            <div className="flex items-center gap-1 text-xs leading-[1.7] text-[#8c8c8c]">
                <span>{t('central.orderQuota.rulesBrief')}</span>
                <Tooltip
                    title={<LineList lines={(t(`central.orderQuota.rules.${strategyType}`) as string).split('\n')} />}
                    overlayStyle={{ maxWidth: 520 }}
                >
                    <QuestionCircleOutlined className="cursor-help" aria-label={t('central.orderQuota.rulesDetail') as string} />
                </Tooltip>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <span>{t(`central.orderQuota.rowCount.${strategyType}`, { count: rows.length })}</span>
                    <Input.Search
                        allowClear
                        className="w-60"
                        placeholder={t(`central.orderQuota.searchTarget.${strategyType}`) as string}
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                    />
                </div>
                <Button onClick={() => setImportOpen(true)}>{t('central.orderQuota.actions.import')}</Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <Button icon={<LeftOutlined />} onClick={() => setStartDate(shiftDate(detail.startDate, -7))}>
                    {t('central.orderQuota.dates.prevWeek')}
                </Button>
                <Button onClick={() => setStartDate(shiftDate(detail.startDate, 7))}>
                    {t('central.orderQuota.dates.nextWeek')} <RightOutlined />
                </Button>
                <DatePicker
                    allowClear={false}
                    value={dayjs(detail.startDate)}
                    onChange={(value) => value && setStartDate(value.format('YYYY-MM-DD'))}
                />
                <Button onClick={() => setStartDate(undefined)}>{t('central.orderQuota.dates.today')}</Button>
                <Typography.Text type="secondary">{t('central.orderQuota.dates.ptHint')}</Typography.Text>
            </div>
            <div className={detail.enabled ? '' : 'opacity-60'}>
                <Spin spinning={loading}>
                    <OrderQuotaGrid
                        detail={detail}
                        rows={visibleRows}
                        dateColumns={dateColumns}
                        expandedTargetId={expandedTargetId}
                        onToggleExpand={toggleExpand}
                        onBatch={openBatch}
                        onTogglePaused={isSorting ? undefined : togglePaused}
                        onCommitDailyQuota={commitDailyQuota}
                        onCommitOverride={commitOverride}
                    />
                </Spin>
            </div>
            <BatchOverrideModal
                open={batchOpen}
                row={selectedRow}
                today={detail.today}
                maxValue={rowMaxValue(selectedRow, detail)}
                onCancel={() => setBatchOpen(false)}
                onSubmit={submitBatch}
            />
            <ImportQuotaModal
                open={importOpen}
                detail={{ ...detail, rows }}
                dates={dateColumns.map((column) => column.date)}
                onCancel={() => setImportOpen(false)}
                onImport={submitImport}
            />
        </div>
    );
};

export default OrderQuotaWorkspace;
